import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import { clearActiveLeague, deleteLeague, getMgmtLeague, getRuleset, putMgmtLeague, putRuleset, type LeaguePatch, type LeagueSettings as LS } from '../../core/api';
import type { AdminRuleset, Pace } from '../../core/types';
import { SCORING_PRESETS, matchPreset, scoringLine } from '../scoringPresets';
import { inviteMessage } from '../format';
import s from './LeagueSettings.module.css';

// The league owner's control panel — GET/PUT /api/mgmt/league. Each control saves on
// change; the backend wires every setting to real behaviour (chat on/off, rounds
// mode, join cap, upcoming-round visibility, allowed round paces). Reached from Admin.

const PACES: { key: Pace; label: string; note: string }[] = [
  { key: 'rapid', label: 'Rapid', note: '24 hours a phase' },
  { key: 'steady', label: 'Steady', note: '3 days a phase' },
  { key: 'slow', label: 'Slow', note: '1 week a phase' },
];

function Toggle({ on, onChange, busy }: { on: boolean; onChange: (v: boolean) => void; busy: boolean }) {
  return (
    <button className={`${s.sw}${on ? ' ' + s.swon : ''}`} onClick={() => onChange(!on)} disabled={busy} aria-pressed={on} aria-label="toggle" />
  );
}

export function LeagueSettings() {
  const nav = useNav();
  const navigate = useNavigate();
  const load = useAsync<LS>(getMgmtLeague, []);
  const rload = useAsync<AdminRuleset>(getRuleset, []);
  const [cfg, setCfg] = useState<LS | null>(null);
  const [rs, setRs] = useState<AdminRuleset | null>(null);
  const [name, setName] = useState('');
  const [max, setMax] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (load.data) {
      setCfg(load.data);
      setName(load.data.name);
      setMax(load.data.max_participants ? String(load.data.max_participants) : '');
    }
  }, [load.data]);

  useEffect(() => { if (rload.data) setRs(rload.data); }, [rload.data]);

  async function save(patch: LeaguePatch, key: string) {
    setSaving(key); setErr(null);
    try {
      const u = await putMgmtLeague(patch);
      setCfg(u);
      setName(u.name);
      setMax(u.max_participants ? String(u.max_participants) : '');
    } catch {
      setErr('Could not save that — try again.');
    } finally {
      setSaving(null);
    }
  }

  async function saveRs(patch: Partial<AdminRuleset>, key: string) {
    setSaving(key); setErr(null);
    try {
      setRs(await putRuleset(patch));
    } catch {
      setErr('Could not save that — try again.');
    } finally {
      setSaving(null);
    }
  }

  if (load.loading || !cfg) {
    return <Shell><div className={s.state}>{load.error ? 'Only the league owner can change these settings.' : 'Loading settings…'}</div></Shell>;
  }

  return (
    <Shell>
      <div className={s.appbar}>
        <button className={s.back} onClick={() => nav?.go('admin')}>← Admin</button>
      </div>
      <div className={s.head}>
        <h2>League settings</h2>
        <p>{cfg.player_count} player{cfg.player_count === 1 ? '' : 's'} · you’re the owner</p>
      </div>

      <div className={s.sec}>League status</div>
      <div className={s.card}>
        <div className={s.row}>
          <span className={s.rl}><b>{cfg.is_active ? 'Live' : 'Paused'}</b><small>{cfg.is_active ? 'Players can reach the league and play.' : 'Everyone but you sees a holding page until you switch it back on.'}</small></span>
          <Toggle on={cfg.is_active} busy={saving === 'status'} onChange={(v) => save({ is_active: v }, 'status')} />
        </div>
      </div>

      <div className={s.sec}>Basics</div>
      <div className={s.card}>
        <div className={s.row}>
          <span className={s.rl}><b>League name</b></span>
          <span className={s.rc}>
            <input className={s.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
            <button className={s.save} disabled={saving === 'name' || !name.trim() || name.trim() === cfg.name} onClick={() => save({ name: name.trim() }, 'name')}>{saving === 'name' ? '…' : 'Save'}</button>
          </span>
        </div>
        <div className={s.row}>
          <span className={s.rl}><b>Invite code</b><small>Share this so people can join</small></span>
          <span className={s.rc}>
            <span className={s.code}>{cfg.join_code}</span>
            <button className={s.ghost} onClick={() => navigator.clipboard?.writeText(inviteMessage(cfg.join_code))}>Copy invite</button>
            <button className={s.ghost} disabled={saving === 'code'} onClick={() => save({ regenerate_join_code: true }, 'code')}>{saving === 'code' ? '…' : 'New code'}</button>
          </span>
        </div>
      </div>

      <div className={s.sec}>How rounds are made</div>
      <div className={s.types}>
        <button className={`${s.type}${cfg.league_type === 'dj' ? ' ' + s.typeon : ''}`} disabled={saving === 'type'} onClick={() => save({ league_type: 'dj' }, 'type')}>
          <b>DJ mode</b>
          <small>You, the owner, build every round.</small>
        </button>
        <button className={`${s.type}${cfg.league_type === 'jukebox' ? ' ' + s.typeon : ''}`} disabled={saving === 'type'} onClick={() => save({ league_type: 'jukebox' }, 'type')}>
          <b>Jukebox mode</b>
          <small>Members propose rounds; they’re drawn at random.</small>
        </button>
      </div>

      <div className={s.sec}>Round pace</div>
      <p className={s.secnote}>Which speeds rounds may run at. Pick one for a fixed pace, or several to let it vary{cfg.league_type === 'jukebox' ? ' (members choose from these when proposing)' : ''}. At least one.</p>
      <div className={s.paces}>
        {PACES.map((p) => {
          const on = cfg.allowed_paces.includes(p.key);
          const last = on && cfg.allowed_paces.length === 1;
          return (
            <button
              key={p.key}
              className={`${s.type}${on ? ' ' + s.typeon : ''}`}
              disabled={saving === 'pace' || last}
              title={last ? 'Keep at least one pace' : undefined}
              onClick={() => save({ allowed_paces: on ? cfg.allowed_paces.filter((x) => x !== p.key) : [...cfg.allowed_paces, p.key] }, 'pace')}
            >
              <b>{p.label}</b>
              <small>{p.note}</small>
            </button>
          );
        })}
      </div>

      <div className={s.sec}>Scoring</div>
      {rs ? (
        <>
          <p className={s.secnote}>
            {matchPreset(rs)
              ? `Currently ${matchPreset(rs)!.name} — ${scoringLine(rs)}.`
              : `Currently custom — ${scoringLine(rs)}. Pick a preset to reset it, or fine-tune the raw numbers in the classic admin scoring tab.`}
          </p>
          <div className={s.presets}>
            {SCORING_PRESETS.map((p) => {
              const on = matchPreset(rs)?.key === p.key;
              return (
                <button key={p.key} className={`${s.type}${on ? ' ' + s.typeon : ''}`} disabled={saving === 'scoring'} onClick={() => saveRs(p.nums, 'scoring')}>
                  <b>{p.name}</b>
                  <small>{scoringLine(p.nums)}</small>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <p className={s.secnote}>{rload.error ? 'Scoring is owner-only and couldn’t be loaded.' : 'Loading scoring…'}</p>
      )}

      <div className={s.sec}>Options</div>
      <div className={s.card}>
        {rs && typeof rs.veto_enabled === 'boolean' ? (
          <div className={s.row}>
            <span className={s.rl}><b>Veto</b><small>Let players veto a track — it’s knocked out at {rs.veto_threshold} vetoes.</small></span>
            <Toggle on={rs.veto_enabled} busy={saving === 'veto'} onChange={(v) => saveRs({ veto_enabled: v }, 'veto')} />
          </div>
        ) : null}
        <div className={s.row}>
          <span className={s.rl}><b>Chat</b><small>Let players talk in the league chat.</small></span>
          <Toggle on={cfg.chat_enabled} busy={saving === 'chat'} onChange={(v) => save({ chat_enabled: v }, 'chat')} />
        </div>
        <div className={s.row}>
          <span className={s.rl}><b>Show upcoming rounds</b><small>Reveal scheduled rounds before they open. Off stops pre-planning.</small></span>
          <Toggle on={cfg.future_rounds_visible} busy={saving === 'future'} onChange={(v) => save({ future_rounds_visible: v }, 'future')} />
        </div>
        <div className={s.row}>
          <span className={s.rl}><b>Max players</b><small>Leave blank for no limit.</small></span>
          <span className={s.rc}>
            <input className={s.input} type="number" min={2} inputMode="numeric" placeholder="No limit" value={max} onChange={(e) => setMax(e.target.value)} />
            <button className={s.save} disabled={saving === 'max' || String(cfg.max_participants ?? '') === max.trim()} onClick={() => save({ max_participants: max.trim() ? Number(max) : null }, 'max')}>{saving === 'max' ? '…' : 'Save'}</button>
          </span>
        </div>
      </div>

      <div className={s.sec}>Danger zone</div>
      <div className={s.card}>
        <div className={s.row}>
          <span className={s.rl}><b>Delete this league</b><small>Permanently removes the league, its rounds, and all scores. Can’t be undone.</small></span>
          <button className={s.danger} disabled={saving === 'delete'} onClick={async () => {
            if (!window.confirm(`Delete “${cfg.name}”? This permanently removes the league and everyone’s history. This can’t be undone.`)) return;
            setSaving('delete'); setErr(null);
            try { await deleteLeague(cfg.league_id); clearActiveLeague(); navigate('/leagues'); }
            catch { setErr('Could not delete the league — try again.'); setSaving(null); }
          }}>{saving === 'delete' ? 'Deleting…' : 'Delete league'}</button>
        </div>
      </div>

      {err ? <div className={s.err}>{err}</div> : null}
    </Shell>
  );
}
